/**
 * iter644 — Extracted from MenuItemsPage.jsx (was lines ~1000-1080) so
 * the Foodics-style list row can be reasoned about / unit-tested in
 * isolation. Pure presentational + handler-prop driven; no API calls
 * happen here.
 */
import React from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { UtensilsCrossed, Star, Clock, Building2, Edit, Trash2, ShieldAlert } from 'lucide-react';
import { MenuMarginBadge } from '@/components/menu/MenuMarginBadge';

export function MenuListRow({
  item,
  idx,
  totalRows,
  isSel,
  branchSummary,
  priceEdit,
  setPriceEdit,
  savePriceEdit,
  changeCategory,
  toggleAvailability,
  toggleSelect,
  handleEdit,
  handleDelete,
  getImageUrl,
  cur,
  categories,
  recipeCost = null,
}) {
  const isLast = idx === totalRows - 1;
  return (
    <div
      className={`grid grid-cols-[36px_56px_minmax(0,2.5fr)_minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1.4fr)_120px_92px] gap-3 items-center px-4 py-3 border-b border-border/60 dark:border-stone-700/60 hover:bg-stone-50/60 dark:hover:bg-stone-900/40 transition ${isSel ? 'bg-primary/5 dark:bg-primary/10' : ''} ${isLast ? 'border-b-0' : ''}`}
      data-testid={`menu-row-${item.id}`}
    >
      <Checkbox checked={isSel} onCheckedChange={() => toggleSelect(item.id)} data-testid={`row-select-${item.id}`} />
      <div className="w-10 h-10 rounded-lg overflow-hidden bg-gradient-to-br from-stone-100 to-stone-200 dark:from-stone-700 dark:to-stone-600 flex items-center justify-center shrink-0">
        {item.image_url ? (
          <img src={getImageUrl(item)} alt={item.name} className="w-full h-full object-cover" />
        ) : (
          <UtensilsCrossed size={16} className="text-stone-400" />
        )}
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="font-semibold text-sm truncate text-foreground dark:text-white">{item.name}</span>
          {item.tags?.includes('popular') && <Star size={11} className="text-amber-500 fill-amber-500 shrink-0" />}
          {item.schedule?.enabled && <Clock size={11} className="text-blue-500 shrink-0" title="Scheduled" />}
          {item.available_online !== false && (item.allergens || []).length === 0 && !item.allergens_reviewed && (
            <span className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 shrink-0"
              title="Allergens not reviewed — will ship to the online menu untagged. Open the item to review." data-testid={`allergen-compliance-badge-${item.id}`}>
              <ShieldAlert size={9} />Allergens?
            </span>
          )}
        </div>
        {item.name_ar && <span className="text-[11px] text-muted-foreground truncate block" dir="rtl">{item.name_ar}</span>}
      </div>
      <select
        value={item.category || ''}
        onChange={(e) => changeCategory(item, e.target.value)}
        onClick={(e) => e.stopPropagation()}
        className="text-xs text-muted-foreground truncate hidden md:block bg-transparent border-0 rounded-md px-1.5 py-1 hover:bg-primary/5 cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary appearance-none"
        title="Click to change category"
        data-testid={`category-select-${item.id}`}
      >
        {categories.map((c) => (
          <option key={c.id} value={c.id}>{c.name}</option>
        ))}
      </select>
      {priceEdit.id === item.id ? (
        <Input
          type="number"
          step="0.01"
          min="0"
          autoFocus
          value={priceEdit.value}
          onChange={(e) => setPriceEdit((p) => ({ ...p, value: e.target.value }))}
          onBlur={savePriceEdit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); savePriceEdit(); }
            else if (e.key === 'Escape') { setPriceEdit({ id: null, value: '', saving: false }); }
          }}
          disabled={priceEdit.saving}
          className="h-8 text-right tabular-nums text-sm font-semibold rounded-md border-primary focus-visible:ring-1 focus-visible:ring-primary"
          data-testid={`price-edit-input-${item.id}`}
        />
      ) : (
        <div className="flex flex-col items-end gap-0.5 min-w-0">
          <button
            type="button"
            onClick={() => setPriceEdit({ id: item.id, value: String(item.price ?? 0), saving: false })}
            className="text-sm font-semibold text-primary text-right tabular-nums w-full px-1.5 py-1 rounded-md hover:bg-primary/5 transition cursor-pointer"
            title="Click to edit price"
            data-testid={`price-cell-${item.id}`}
          >
            {cur()} {Number(item.price || 0).toFixed(2)}
          </button>
          <MenuMarginBadge price={item.price} cost={recipeCost} />
        </div>
      )}
      <div className="flex items-center gap-1 min-w-0 hidden md:flex">
        <Building2 size={11} className="text-muted-foreground shrink-0" />
        <span className="text-[11px] text-muted-foreground truncate">{branchSummary}</span>
      </div>
      <button
        type="button"
        onClick={() => toggleAvailability(item)}
        className={`text-[10px] font-semibold px-2 py-0.5 rounded-full inline-flex items-center justify-center w-fit cursor-pointer hover:ring-2 hover:ring-offset-1 transition ${item.is_available ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 hover:ring-emerald-300' : 'bg-stone-200 text-stone-600 dark:bg-stone-700 dark:text-stone-300 hover:ring-stone-300'}`}
        title="Click to toggle availability"
        data-testid={`status-pill-${item.id}`}
      >
        {item.is_available ? 'Active' : 'Inactive'}
      </button>
      <div className="flex justify-end gap-1">
        <Button size="icon" variant="ghost" className="h-8 w-8 rounded-lg text-muted-foreground hover:text-primary" onClick={() => handleEdit(item)} data-testid={`edit-${item.id}`}><Edit size={13} /></Button>
        <Button size="icon" variant="ghost" className="h-8 w-8 rounded-lg text-muted-foreground hover:text-rose-600" onClick={() => handleDelete(item)} data-testid={`delete-${item.id}`}><Trash2 size={13} /></Button>
      </div>
    </div>
  );
}
